import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MinusCircleOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, Alert, Button, DatePicker, Select, Row, Col, Table, List, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	requestsForProposalRestCrudThunksForBuyer,
	requestsForProposalRestCrudThunksForSupplier,
} from '../../thunks/requests_for_proposal_thunks';
import moment from 'moment';
import {
	compareStrings,
	getObjectValues,
	isInternalTeam,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import FileUploader from '../file_uploader/FileUploader';
import { capitalize, capitalizeEachWord } from '../../utils/DataFormatterUtils';
import WorkOrderRowDisplay from '../work_order_row_display/WorkOrderRowDisplay';
import { Link } from 'react-router-dom';
import { FlexibleImageAvatar } from '../flexible-image-avatar/FlexibleImageAvatar';
import ClientShowMoreList from '../ClientShowMoreList/ClientShowMoreList';
import { SUPPLIER_PRIVATE_NETWORKS_CRUD_ACTION_CREATORS } from '../../actions/supplier_private_networks_actions';
import {
	supplierPrivateNetworksV2WithSupplierFacilityContactsForBuyer,
	supplierPrivateNetworksV2WithSupplierFacilityContactsForSupplier,
} from '../../thunks/supplier_private_networks_thunks';
import { BIDDERS_CRUD_ACTION_CREATORS } from '../../actions/bidders_actions';
import {
	CURRENCIES,
	filterCurrencyOption,
	FILTER_CONFIG_NAMES,
	ROLE_TYPES,
} from '../../utils/DataConstants';
import OWRichTextEditor from '../../rich_text_editor/OWRichTextEditor';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import {
	problemTypesHeirarchical,
	problemTypesRestCrudThunksForBuyer,
	problemTypesRestCrudThunksForSupplier,
} from '../../thunks/problem_types_thunks';
import ProblemTypeTreeSelect from '../problem_type_tree_select';

require('./RequestForProposalForm.less');
const SUPPLIER_PRIVATE_NETWORK_ADVANCED_FILTERS = 'supplierPrivateNetworkAdvancedFilters';
const SUPPLIER_PRIVATE_NETWORKS_INDEX_TARGET_COLLECTION = 'supplierPrivateNetworksIndex';

const defaultPagination = { current: 1, pageSize: 4 };

const DATE_FORMAT = 'MM-DD-YYYY';

const RequestForProposalForm: FC<any> = ({
	form,
	formData,
	history,
	saveFormData,
	userType,
	creating,
	updating,
	update,
	create,
	createErrors,
	updateErrors,
	updateSupplierFacilityFilters,
	clearAndUpdateFilters,
	supplierPrivateNetworks,
	locations,
	fetchLocations,
	fetchMultipleLocations,
	problemTypes,
	fetchProblemTypes,
	fetchMultipleProblemTypes,
}): React.ReactElement => {
	const [bidders, setBidders] = useState(formData.bidders || []);
	const [bidderAdded, setBidderAdded] = useState(false);

	const { setFieldsValue } = form;

	const editorRef = useRef(null);

	const handleFileAttachmentUploadChange = useCallback(
		(newAttachments) => {
			setFieldsValue({ attachments: newAttachments });
		},
		[setFieldsValue]
	);

	const getDescription = useCallback(
		() => nullSafeGet('current', editorRef) && editorRef.current.getContent(),
		[]
	);

	const handleSubmit = useCallback(
		(e, submitAction) => {
			if (e) {
				e.preventDefault();
			}

			form.validateFields((err, values) => {
				if (err) {
					return;
				}

				const requestForProposal = {
					...values,
					description: getDescription(),
					workOrderId: parseInt(formData.workOrderId, 10),
					incomingBidders: bidders.map((b) => ({
						supplierFacilityId: b.supplierFacilityId,
						primaryEmails: b.primaryEmails || [],
					})),
				};

				submitAction(requestForProposal)
					.then((rfp) => history.push(`/${userType}/requestsForProposal/detail/${rfp.id}`))
					.catch(message.error);
			});
		},
		[bidders, form, formData, getDescription, history, userType]
	);

	const isUpdate = useMemo(
		() => formData.hasOwnProperty('id') && formData.id !== undefined,
		[formData]
	);
	const submitText = useMemo(() => (isUpdate ? 'Update event' : 'Create event'), [isUpdate]);
	const submitAction = useMemo(() => (isUpdate ? update : create), [create, isUpdate, update]);
	const submitting = useMemo(() => creating || updating, [creating, updating]);

	const currencyRecordsList = useMemo(() => getObjectValues(CURRENCIES), [CURRENCIES]);

	const dueDateValidator = useCallback((rule, value, callback) => {
		const now = moment();
		if (value && value < now) {
			callback('Due date must be in the future.');
		}
		callback();
	}, []);

	const disabledDate = useCallback((current) => {
		// Can not select days before today and today
		return current && current < moment().endOf('day');
	}, []);

	const errorMessagesText = useMemo(
		() => createErrors.concat(updateErrors).join('; '),
		[createErrors, updateErrors]
	);

	const addBidder = useCallback(
		(supplierFacility, requestForProposalRoutedTo) => {
			const bidder = bidders.find((bidder) => bidder.supplierFacilityId === supplierFacility.id);
			const otherBidders = bidders.filter(
				(bidder) => bidder.supplierFacilityId !== supplierFacility.id
			);
			if (bidder) {
				const newBidder = {
					...bidder,
					primaryEmails: bidder.primaryEmails || [],
					primaryContacts: bidder.primaryContacts,
				};
				setBidders([...otherBidders, newBidder]);
			} else {
				const newBidder = {
					supplierFacilityId: supplierFacility.id,
					supplierFacility: supplierFacility,
					requestForProposalRoutedTo,
					primaryEmails: [],
					primaryContacts: [],
					bidderStatus: 'noResponse',
					awardStatus: 'pending',
				};
				setBidders([...otherBidders, newBidder]);
			}
		},
		[bidders]
	);

	const removeBidder = useCallback(
		(bidderAssoc) => {
			const { bidder, otherBidders } = bidders.reduce(
				(acc, b) => {
					return b.supplierFacilityId === bidderAssoc.supplierFacilityId
						? { ...acc, bidder: b }
						: { ...acc, otherBidders: [...acc.otherBidders, b] };
				},
				{
					bidder: undefined,
					otherBidders: [],
				}
			);
			const newBidder = {
				...bidder,
				primaryEmails: bidderAssoc.primaryEmails || [],
				primaryContacts: bidderAssoc.primaryContacts || [],
			};
			if (newBidder.hasOwnProperty('createdAt')) {
				if (!formData.removedBidders) {
					formData.removedBidders = [];
				}
				formData.removedBidders.push(newBidder);
			}
			setBidders(otherBidders);
			saveFormData(formData);
		},
		[bidders, formData, saveFormData]
	);

	const hasBidder = useCallback(
		(supplierFacility) => {
			return bidders && bidders.some((el) => el.supplierFacilityId === supplierFacility.id);
		},
		[bidders]
	);

	const workOrder = useMemo(() => formData.workOrder || {}, [formData.workOrder]);

	useEffect(() => {
		const { supplierFacility } = workOrder;
		const isExternalSupplier = supplierFacility && !isInternalTeam(supplierFacility);
		if (isExternalSupplier && !bidderAdded) {
			addBidder(
				supplierFacility,
				nullSafeGet('supplierFacility.requestForProposalRoutedTo', workOrder)
			);
			setBidderAdded(true);
		}
	}, [addBidder, bidderAdded, workOrder]);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'supplierFacilityId',
				render: (_, record) => (
					<div>
						<Link to={`/${userType}/suppliers/detail/${record.supplierFacilityId}`} target="_blank">
							<div style={{ display: 'flex', alignItems: 'center' }}>
								<div style={{ marginRight: 24 }}>
									<FlexibleImageAvatar
										displayName={nullSafeGetOrElse('supplierFacility.displayName', record, '')}
										showPopover={false}
										popoverText={nullSafeGetOrElse('supplierFacility.displayName', record, '')}
										width="48px"
										height="48px"
										imageUrl={record.supplierFacility.logoURL}
									/>
								</div>
								<div>
									<div
										style={{
											fontSize: 18,
											wordBreak: 'break-word',
										}}
									>
										{record.supplierFacility.displayName}
									</div>
								</div>
							</div>
						</Link>
						<div style={{ marginTop: 16, marginLeft: 72 }}>
							<ClientShowMoreList
								dataSource={[record]}
								itemLayout="horizontal"
								showAll={true}
								initialNumVisibleRecords={3}
								renderItem={(item) => (
									<List.Item
										actions={[
											<Button
												type="primary"
												disabled={hasBidder(record.supplierFacility)}
												onClick={() =>
													addBidder(record.supplierFacility, record.requestForProposalRoutedTo)
												}
											>
												{hasBidder(record) ? 'Invited' : 'Invite'}
											</Button>,
										]}
										extra=""
									>
										<List.Item.Meta
											avatar={null}
											title={null}
											description={nullSafeGetOrElse('requestForProposalRoutedTo', record, []).join(
												', '
											)}
										/>
										<div></div>
									</List.Item>
								)}
							/>
						</div>
					</div>
				),
			},
		],
		[addBidder, hasBidder, userType]
	);

	const columns2 = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'displayName',
				render: (_, record) => (
					<div>
						<div className="flex flex-row items-center justify-between">
							<Link to={`/${userType}/suppliers/detail/${record.id}`} target="_blank">
								<div style={{ display: 'flex', alignItems: 'center' }}>
									<div style={{ marginRight: 24 }}>
										<FlexibleImageAvatar
											displayName={nullSafeGetOrElse('supplierFacility.displayName', record, '')}
											showPopover={false}
											popoverText={nullSafeGetOrElse('supplierFacility.displayName', record, '')}
											width="48px"
											height="48px"
											imageUrl={record.supplierFacility.logoURL}
										/>
									</div>
									<div>
										<div
											style={{
												fontSize: 18,
												wordBreak: 'break-word',
											}}
										>
											{record.supplierFacility.displayName}
										</div>
									</div>
								</div>
							</Link>
							{nullSafeGet('supplierFacilityId', workOrder) === record.supplierFacilityId ? null : (
								<MinusCircleOutlined
									className="dynamic-delete-button"
									onClick={() => removeBidder(record)}
								/>
							)}
						</div>
						<div style={{ marginTop: 16, marginLeft: 72 }}>
							<ClientShowMoreList
								dataSource={[record]}
								itemLayout="horizontal"
								showAll={true}
								initialNumVisibleRecords={3}
								renderItem={(item) => (
									<List.Item extra="">
										<List.Item.Meta
											avatar={null}
											title={
												<span>
													{item.nameGiven && item.nameFamily
														? capitalizeEachWord(
																[item.nameGiven, item.nameFamily, item.suffix].join(' ')
														  )
														: item.email}
												</span>
											}
											description={nullSafeGetOrElse('requestForProposalRoutedTo', record, []).join(
												', '
											)}
										/>
										<div></div>
									</List.Item>
								)}
							/>
						</div>
					</div>
				),
			},
		],
		[removeBidder, userType, workOrder]
	);

	const filterConfig = useMemo(
		() => [
			{
				label: 'Location',
				fieldName: FILTER_CONFIG_NAMES.LOCATION,
				mode: 'multiple',
				stateSlice: locations,
				targetCollectionName: SUPPLIER_PRIVATE_NETWORK_ADVANCED_FILTERS,
				fetch: fetchLocations,
				fetchMultiple: fetchMultipleLocations,
			},
			{
				label: 'Problem Type',
				fieldName: FILTER_CONFIG_NAMES.PROBLEM_TYPE,
				mode: 'multiple',
				stateSlice: problemTypes,
				targetCollectionName: SUPPLIER_PRIVATE_NETWORK_ADVANCED_FILTERS,
				fetch: fetchProblemTypes,
				fetchMultiple: fetchMultipleProblemTypes,
				renderRecord: (pt) => (
					<Select.Option key={pt.id} value={pt.id}>
						{pt.hierarchyName}
					</Select.Option>
				),
				component: ProblemTypeTreeSelect,
			},
		],
		[
			fetchLocations,
			fetchMultipleLocations,
			fetchMultipleProblemTypes,
			fetchProblemTypes,
			locations,
			problemTypes,
		]
	);

	return (
		<Form className="requestForProposalForm" layout="vertical">
			{form.getFieldDecorator('id', { initialValue: formData.id })}
			{form.getFieldDecorator('status', { initialValue: formData.status || 'draft' })}

			<div style={{ marginBottom: 32 }}>
				<WorkOrderRowDisplay workOrder={workOrder} />
			</div>
			{createErrors.length > 0 || updateErrors.length > 0 ? (
				<Form.Item>
					<Alert message={errorMessagesText} type="error" />
				</Form.Item>
			) : null}
			<Form.Item label="Title">
				{form.getFieldDecorator('title', {
					initialValue: formData.title || nullSafeGet('workOrder.title', formData),
					rules: [{ required: true, message: 'Please give your request for proposal a title.' }],
				})(<Input placeholder="Give your request for proposal a title." />)}
			</Form.Item>
			<Form.Item label="Currency">
				{form.getFieldDecorator('currencyId', {
					rules: [{ required: true, message: 'Please select a currency for this event.' }],
					initialValue: formData.currencyId || 'USD',
				})(
					<Select
						showSearch={true}
						style={{ width: 300 }}
						optionFilterProp="children"
						filterOption={filterCurrencyOption}
						placeholder="Currency"
					>
						{currencyRecordsList.map((currency) => (
							<Select.Option
								key={currency.id}
								value={currency.id}
								disabled={
									!compareStrings(currency.id, 'usd') && !compareStrings(currency.id, 'cad')
								}
							>
								{capitalize(currency.displayNamePlural)} ({currency.id})
							</Select.Option>
						))}
					</Select>
				)}
			</Form.Item>
			<Form.Item label="Due Date">
				{form.getFieldDecorator('dueDate', {
					rules: [
						{
							type: 'object',
							required: true,
							message: 'Please select a due date for RFP responses!',
						},
						{ validator: dueDateValidator },
					],
					initialValue: formData.dueDate ? moment(formData.dueDate) : undefined,
				})(<DatePicker disabledDate={disabledDate} format={DATE_FORMAT} />)}
			</Form.Item>
			<Form.Item label="Additional details">
				<OWRichTextEditor
					initialEditorState={
						formData.description || nullSafeGet('workOrder.description', formData)
					}
					editorRef={editorRef}
				/>
			</Form.Item>
			<Form.Item label="File Attachments" style={{ width: 400 }}>
				{form.getFieldDecorator('attachments', { initialValue: formData.attachments || [] })(
					<FileUploader
						defaultFileList={formData.attachments || []}
						convertedImages={nullSafeGet('convertedImages', formData)}
						roleType={userType}
						handleUploadSuccess={handleFileAttachmentUploadChange}
						showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
						uploaderType="dragger"
					/>
				)}
			</Form.Item>
			<Form.Item label="Bidders" style={{ width: 1000 }}>
				<Row style={{ margin: 0 }} gutter={16}>
					<Col span={12}>
						<div style={{ width: '100%' }}>
							{createErrors.length > 0 || updateErrors.length > 0 ? (
								<div style={{ marginBottom: 24 }}>
									<Alert message={errorMessagesText} type="error" />
								</div>
							) : null}
							{bidders && bidders.length > 0 ? (
								<div style={{ minHeight: '473px' }}>
									<Table
										columns={columns2}
										showHeader={false}
										rowKey={(el) => el['supplierFacilityId']}
										dataSource={bidders}
									/>
								</div>
							) : (
								<div style={{ minHeight: '473px' }}>
									<div style={{ fontSize: '16px', margin: '16px 0' }}>
										You haven't added any bidders to this event yet.
									</div>
									<div style={{ display: 'flex', justifyContent: 'center' }}>
										<img
											style={{ margin: '32px 0', maxWidth: '400px' }}
											width="100%"
											height="auto"
											src="https://gw.alipayobjects.com/zos/rmsportal/KpnpchXsobRgLElEozzI.svg"
											alt="You haven't added any bidders to this event yet."
										/>
									</div>
								</div>
							)}
						</div>
					</Col>
					<Col span={12} style={{ padding: '0 0 0 40px' }}>
						<PaginatedReduxTableWithHeader
							targetCollectionName={SUPPLIER_PRIVATE_NETWORKS_INDEX_TARGET_COLLECTION}
							showHeader={false}
							useDistinctCollection={true}
							updateFilters={updateSupplierFacilityFilters}
							stateSlice={supplierPrivateNetworks}
							clearAndUpdateFilters={clearAndUpdateFilters}
							filterConfig={filterConfig}
							entityCollectionName="supplier_private_networks"
							tableColumns={columns}
							keyAccessor={(el) => el.supplierFacilityId}
							initialPagination={defaultPagination}
							initialFilters={{
								problemTypeIds: nullSafeGet('workOrder.problemTypeId', formData),
								locationIds: nullSafeGet('workOrder.locationId', formData),
							}}
							fetchData={
								userType === ROLE_TYPES.SUPPLIER
									? supplierPrivateNetworksV2WithSupplierFacilityContactsForSupplier
									: supplierPrivateNetworksV2WithSupplierFacilityContactsForBuyer
							}
							advancedFiltersInputParamName="supplierFacilityAlias"
							ignoreLocationQueryParams
						/>
					</Col>
				</Row>
			</Form.Item>
			<Form.Item label="">
				<Button loading={submitting} type="primary" onClick={(e) => handleSubmit(e, submitAction)}>
					{submitText}
				</Button>
				<Button className="ml-2" loading={submitting} onClick={() => history.goBack()}>
					Cancel
				</Button>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	creating: state.requests_for_proposal.creating,
	updating: state.requests_for_proposal.updating,
	createErrors: state.requests_for_proposal.createErrors,
	updateErrors: state.requests_for_proposal.updateErrors,
	currentUser: state.session.currentUser,
	supplierPrivateNetworks: state.supplier_private_networks,
	locations: state.locations,
	problemTypes: state.problem_types,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateSupplierFacilityFilters: (filters, targetCollection) =>
		dispatch(
			SUPPLIER_PRIVATE_NETWORKS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)
		),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(
			SUPPLIER_PRIVATE_NETWORKS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(
				filters,
				targetCollectionName
			)
		),
	saveFormData: (formData) => dispatch(BIDDERS_CRUD_ACTION_CREATORS.saveFormData(formData)),
	create: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestsForProposalRestCrudThunksForSupplier.create(entity)
				: requestsForProposalRestCrudThunksForBuyer.create(entity)
		),
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestsForProposalRestCrudThunksForSupplier.update(entity)
				: requestsForProposalRestCrudThunksForBuyer.update(entity)
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: locationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) => {
		const fnToCall =
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readLite
				: locationsRestCrudThunksForBuyer.readLite;
		return dispatch(
			fnToCall(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		);
	},
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? problemTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchProblemTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			problemTypesHeirarchical(ownProps.userType)(
				{ ...params, no_pagination: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

const RFPFOrm = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(Form.create<any>()(RequestForProposalForm))
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(RFPFOrm);
